import { useCallback, useEffect, useRef, useState } from 'react';
import type { PromotionList } from '@/api/generated';
import { ApiError } from '@/api/errors';
import { rateCardsApi, type GrantedCard } from '@/api/rateCards';
import type { TenantApi } from '@/api/tenantApi';
import { useRemote } from '@/components/useRemote';
import { useTenantApi } from '@/tenant/useTenant';

export type PromotionStatusChange = 'ACTIVE' | 'PAUSED' | 'ENDED';

export interface PromotionsData {
  promotions: PromotionList;
  /**
   * The adopted granted card: its base retail and wholesale are the cost card's (TMMAPI-0155);
   * `null` when none is adopted or the list failed.
   */
  tenantCard: GrantedCard | null;
}

export interface PromotionsState {
  status: 'loading' | 'ready' | 'error';
  data: PromotionsData | null;
  error: ApiError | null;
  reload: () => void;
  setStatus: (id: number, status: PromotionStatusChange) => Promise<void>;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

/** The adopted granted card; none adopted, or the list failing, is `null`. */
async function loadCostCards(): Promise<Pick<PromotionsData, 'tenantCard'>> {
  const granted = await rateCardsApi.listGrantedCards().then(
    (cards) => cards.find((card) => card.adopted) ?? null,
    () => null,
  );
  return { tenantCard: granted };
}

async function loadAll(api: TenantApi): Promise<PromotionsData> {
  const [promotions, cards] = await Promise.all([api.listPromotions(), loadCostCards()]);
  return { promotions, ...cards };
}

/** The New promotion page's cost basis: the adopted granted card, once. `null` while loading. */
export function useCostCards(): Pick<PromotionsData, 'tenantCard'> | null {
  const load = useCallback(() => loadCostCards(), []);
  return useRemote(load).data;
}

/**
 * Loads promotions and the adopted granted card. Only the promotions are required; the card
 * failing degrades the cost card alone. StrictMode-safe.
 */
export function usePromotions(): PromotionsState {
  const [status, setStatus] = useState<PromotionsState['status']>('loading');
  const [data, setData] = useState<PromotionsData | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const api = useTenantApi();
  const started = useRef(false);

  const reload = useCallback(() => {
    setStatus('loading');
    setError(null);
    loadAll(api).then(
      (next) => {
        setData(next);
        setError(null);
        setStatus('ready');
      },
      (err: unknown) => {
        setData(null);
        setError(toApiError(err));
        setStatus('error');
      },
    );
  }, [api]);

  const setPromoStatus = useCallback(
    async (id: number, nextStatus: PromotionStatusChange) => {
      if (nextStatus === 'ACTIVE') {
        // A status-only PATCH: every other key is left out, so it is unchanged (TMMAPI-0167).
        await api.patchPromotion(id, { status: 'ACTIVE' });
      } else if (nextStatus === 'PAUSED') {
        await api.pausePromotion(id);
      } else {
        await api.endPromotion(id);
      }
      const next = await loadAll(api);
      setData(next);
      setError(null);
      setStatus('ready');
    },
    [api],
  );

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    reload();
  }, [reload]);

  return { status, data, error, reload, setStatus: setPromoStatus };
}
